import type { ActivityMessage } from "@/api/models"

/** `Details` is not exported from the models module; borrow it off the field. */
type ActivityDetails = NonNullable<ActivityMessage["details"]>

/** Activity lines that describe transport plumbing rather than user-visible work. */
const HIDDEN_ACTIVITY_TYPES = new Set([
  "heartbeat",
  "ping",
  "token_usage",
  "usage_metadata",
  "reasoning_summary_live",
])

/** Keep a single spoken-length line; the feed is glanceable, not a log viewer. */
const MAX_ACTIVITY_MESSAGE_LENGTH = 160

function coerceDetails(value: unknown): ActivityDetails | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined
  return value as ActivityDetails
}

/**
 * Normalize one inbound voice frame into the `ActivityMessage` shape the chat
 * activity translators already understand.
 *
 * Two producers land here. Direct voice tools emit `tool_activity` through the
 * realtime route's own callback, while the nested chat agent emits plain
 * `activity` frames — its `send_activity_update` resolves the outbound writer
 * of whichever socket is in context, which during voice mode is the realtime
 * socket. Both arrive with the same field names, so one normalizer covers them.
 *
 * Returns null for frames with nothing worth showing, so callers can append
 * unconditionally.
 */
export function normalizeVoiceActivityFrame(frame: unknown): ActivityMessage | null {
  if (!frame || typeof frame !== "object") return null
  const candidate = frame as Record<string, unknown>

  const activityType = candidate.activity_type
  if (typeof activityType !== "string" || !activityType) return null
  if (HIDDEN_ACTIVITY_TYPES.has(activityType)) return null

  const details = coerceDetails(candidate.details)
  const translationKey = details?.translation_key
  if (typeof translationKey === "string" && HIDDEN_ACTIVITY_TYPES.has(translationKey)) {
    return null
  }

  const rawMessage = typeof candidate.message === "string" ? candidate.message.trim() : ""
  // An untranslated frame still needs a body: `translateActivityMessage`
  // returns "" when `message` is empty, which would render a blank row.
  if (!rawMessage) return null

  const timestamp = typeof candidate.timestamp === "number" ? candidate.timestamp : Date.now()

  return {
    type: "activity",
    activity_type: activityType,
    message: rawMessage.slice(0, MAX_ACTIVITY_MESSAGE_LENGTH),
    ...(details ? { details } : {}),
    // Carried, not rendered — nothing in the activity UI reads this field, and
    // backend frames measure it in epoch seconds while client-built activities
    // use milliseconds. Pass the frame's own value through rather than
    // inventing a normalization no consumer needs.
    timestamp,
  }
}

/** Whole seconds a tool call has been running, for the overlay's elapsed counter. */
export function elapsedSecondsSince(startedAt: number, now: number): number {
  return Math.max(0, Math.floor((now - startedAt) / 1000))
}
